import {useEffect, useRef, useState} from "react";
import type {CSSProperties, RefObject} from "react";
import {observer, useLocalObservable} from "mobx-react";
import type {IReactionDisposer} from "mobx";
import {reaction} from "mobx";
import styled from "styled-components";
import type {IModel} from "../../../data/models/Model";
import type {IFieldAdapter} from "../../../data/models/field/Field";
import type {IFilterRow, IFilterState} from "../../../data/models/filter/Filter";
import {XyiconFeature} from "../../../generated/api/base";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import {SearchFieldV5} from "../input/search/SearchFieldV5";
import {FilterSwitchV5} from "../widgets/filters/filterSwitch/FilterSwitchV5";
import {ButtonV5} from "../button/ButtonV5";
import {ReactUtils} from "../../utils/ReactUtils";
import {useAppStore} from "../../../StateManager";
import {baseDistance, FlexCenterStyle, fontSize} from "../styles/styles";
import {PopupV5} from "../popup/PopupV5";
import {HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {IconButtonV5} from "../interaction/IconButtonV5";
import CloseIcon from "../icons/xmark.svg?react";
import FilterIcon from "../icons/filter-icon.svg?react";
import {PanelHeaderStyled} from "../modules/spaceeditor/viewbar/LayerPanel.styled";
import {isHiddenViewColumnV5} from "../../../data/models/ViewUtils";
import {AdvancedFilterEditorV5, getApplicableAdvancedFilterRows, hasAdvancedFilterDraftRows} from "./advance/AdvancedFilterEditorV5";
import {FilterEditorStyled} from "./FilterEditor.styled";
import {SimpleFilterEditorV5} from "./simple/SimpleFilterEditorV5";

interface IFilterEditorProps {
	readonly features: XyiconFeature[];
	readonly feature: XyiconFeature;
	readonly items: IModel[];
	readonly loader?: boolean;
	readonly onSelect: (item: IModel[], disableTabChange?: boolean) => void;
	readonly isSpaceEditor?: boolean;
	readonly onClose?: () => void;
	readonly parentRef?: RefObject<HTMLDivElement>;
}

export const FilterEditorV5 = observer((props: IFilterEditorProps) => {
	const appState = useAppStore((state) => state.appState);
	const {feature, features, items, loader, onSelect, isSpaceEditor, onClose, parentRef} = props;

	const selectedView = appState.actions.getSelectedView(feature);
	const [search, setSearch] = useState("");
	const [applyingFilters, setApplyingFilters] = useState(false);
	const reactionDisposerRef = useRef<IReactionDisposer | null>(null);
	const isSyncingRef = useRef(false);
	const viewKey = `${feature}-${selectedView?.id ?? "unknown"}`;

	const simpleEditorRef = useRef<SimpleFilterEditorV5>(null);
	const advancedEditorRef = useRef<AdvancedFilterEditorV5>(null);

	const localFilters = useLocalObservable(() => ({
		simple: ObjectUtils.deepClone(selectedView.filters.filters.simple),
		advanced: ObjectUtils.deepClone(selectedView.filters.filters.advanced),
	}));

	const getFilterEditor = () => {
		return selectedView.filters.type === "advanced" ? advancedEditorRef.current : simpleEditorRef.current;
	};

	const reset = (viewFilters?: IFilterState) => {
		const savedFilters = selectedView.getSavedFilters();
		const filtersToUse = viewFilters || savedFilters;

		localFilters.simple = ObjectUtils.deepClone(filtersToUse.filters.simple);
		localFilters.advanced = ObjectUtils.deepClone(filtersToUse.filters.advanced);

		const activeFilterEditor = getFilterEditor();
		const filters = selectedView.filters.type === "simple" ? filtersToUse.filters.simple : filtersToUse.filters.advanced;

		// If you click the toggle for advanced filtering, it's possible
		// that getFilterEditor will give you the "SimpleFilterEditor" (and vica versa),
		// while the data above already suggests it should be the advanced one
		if (activeFilterEditor?.type === selectedView.filters.type) {
			activeFilterEditor.resetTo(filters);
		}
	};

	const getAdvancedFiltersToApply = () => {
		return getApplicableAdvancedFilterRows(localFilters.advanced);
	};

	const isResetButtonEnabled = () => {
		const savedFilters = selectedView.getSavedFilters();

		const isSimple = selectedView.filters.type === "simple";
		const savedViewFilters = isSimple ? savedFilters.filters.simple : savedFilters.filters.advanced;
		const appliedViewFilters = isSimple
			? selectedView.filters.filters.simple
			: getApplicableAdvancedFilterRows(selectedView.filters.filters.advanced);
		const localViewFilters = isSimple ? localFilters.simple : getAdvancedFiltersToApply();
		const hasDraftRows = !isSimple && hasAdvancedFilterDraftRows(localFilters.advanced);

		return (
			hasDraftRows ||
			JSON.stringify(localViewFilters) !== JSON.stringify(savedViewFilters) ||
			JSON.stringify(appliedViewFilters) !== JSON.stringify(savedViewFilters)
		);
	};

	const syncFilters = () => {
		isSyncingRef.current = true;
		getFilterEditor()?.applyAll?.();
		isSyncingRef.current = false;
		setApplyingFilters(false);
	};

	const syncLocalFilters = (filters: IFilterRow[]) => {
		localFilters.advanced = filters;
	};

	const applyAll = () => {
		setApplyingFilters(true);

		// timeout needed otherwise the loader will not be shown as the UI freezes
		setTimeout(() => {
			syncFilters();
		}, 100);

		appState.tableComponent.current?.onPageChange(0);
		if (![XyiconFeature.Portfolio, XyiconFeature.XyiconCatalog, XyiconFeature.Xyicon, XyiconFeature.Boundary].includes(feature)) {
			onSelect([]);
		}
	};

	const onChangeFilterType = (value: boolean) => {
		const newType = value ? "advanced" : "simple";

		// Save props.filters to localFilters when switching, so the up to date values will be
		// passed to localFilters
		if (newType === "simple") {
			localFilters.simple = ObjectUtils.deepClone(selectedView.filters.filters.simple);
		} else {
			localFilters.advanced = ObjectUtils.deepClone(selectedView.filters.filters.advanced);
		}

		selectedView.filters.type = newType;
	};

	const onResetClick = () => {
		reset();
		sessionStorage.setItem(viewKey, "0");
	};

	const onResetSearch = () => setSearch("");
	const onSearchInput = (value: string) => setSearch(value);

	const renderResetButton = (clearDisabled: boolean) => {
		const resetEnabled = isResetButtonEnabled();
		const clearEnabled = !clearDisabled;

		if (resetEnabled && clearEnabled) {
			return (
				<>
					<ButtonV5
						label="Reset"
						type="secondary"
						disabled={!isResetButtonEnabled()}
						onClick={onResetClick}
					/>
				</>
			);
		}

		if (resetEnabled && !clearEnabled) {
			return (
				<ButtonV5
					type="secondary"
					label="Reset"
					onClick={onResetClick}
				/>
			);
		}

		return (
			<ButtonV5
				type="secondary"
				disabled={true}
				label="Reset"
				onClick={onResetClick}
			/>
		);
	};

	// this is declared after to the reset function
	const resetRef = useRef(reset);
	resetRef.current = reset;

	useEffect(() => {
		resetRef.current(selectedView.filters);
		reactionDisposerRef.current = reaction(
			() => JSON.stringify(selectedView.filters),
			() => {
				if (!isSyncingRef.current) {
					resetRef.current(selectedView.filters);
				}
			},
		);

		return () => {
			reactionDisposerRef.current?.();
			reactionDisposerRef.current = null;
		};
	}, [selectedView.id, selectedView.filters]);

	const fieldFilter = (field: IFieldAdapter) => {
		const refId = field.refId;
		return !(isHiddenViewColumnV5(refId) || refId === `${XyiconFeature.Xyicon}/icon` || refId === `${XyiconFeature.XyiconCatalog}/icon`);
	};

	const filters = selectedView.filters;
	const appliedFilters = filters.type === "simple" ? filters.filters.simple : filters.filters.advanced;
	const local = filters.type === "simple" ? localFilters.simple : localFilters.advanced;
	const isAdvanced = filters.type === "advanced";
	const comparableLocalFilters = isAdvanced ? getAdvancedFiltersToApply() : local;
	const clearDisabled = appliedFilters.length + comparableLocalFilters.length === 0;
	const oldChanges = selectedView.getSavedDataForType("filters", false);
	const newChanges = selectedView.getNewDataForType("filters", false);

	let hasChanges = false;
	if (isAdvanced) {
		hasChanges = JSON.stringify(oldChanges?.filters?.advanced) !== JSON.stringify((newChanges as IFilterState)?.filters?.advanced);
	} else {
		hasChanges = JSON.stringify(oldChanges?.filters?.simple) !== JSON.stringify((newChanges as IFilterState)?.filters?.simple);
	}

	// Checks if two filter arrays are equal, ignoring order inside each filter’s `param` array.
	// Other fields are already ordered consistently, so only `param` needs normalization.
	// This was needed because `JSON.stringify(appliedFilters) === JSON.stringify(local)` this returns false if any array inside those filters has elements in a different order,
	// even if both logically represent the same thing.
	const areFiltersEqual = (a: IFilterRow[], b: IFilterRow[]) => {
		const normalize = (filters: IFilterRow[]) =>
			JSON.stringify(
				filters.map((f) => {
					if (f.type === "filter") {
						const paramValue = f.value.param;
						return {
							field: f.value.field,
							operator: f.value.operator,
							param: Array.isArray(paramValue) ? [...paramValue].sort() : (paramValue ?? null),
						};
					}

					// include separator value for comparison
					if (f.type === "separator") {
						return {
							type: f.type,
							value: f.value,
						};
					}

					return f; // fallback, if new types are added later
				}),
			);

		return normalize(a) === normalize(b);
	};

	const savedFilters = selectedView.getSavedFilters();
	const savedViewFilters = isAdvanced ? savedFilters.filters.advanced : savedFilters.filters.simple;
	const comparableAppliedFilters = isAdvanced ? getApplicableAdvancedFilterRows(appliedFilters) : appliedFilters;
	const showReset =
		hasChanges ||
		(isAdvanced && hasAdvancedFilterDraftRows(localFilters.advanced)) ||
		!areFiltersEqual(savedViewFilters, comparableLocalFilters) ||
		!areFiltersEqual(savedViewFilters, comparableAppliedFilters);

	const headerButtons = (
		<HeaderButtonsStyled>
			{showReset && renderResetButton(clearDisabled)}
			<ButtonV5
				label={applyingFilters ? "Applying" : "Apply"}
				disabled={applyingFilters || areFiltersEqual(appliedFilters, comparableLocalFilters)}
				onClick={applyAll}
			/>
		</HeaderButtonsStyled>
	);

	const filterContent = (
		<FilterEditorStyled className={ReactUtils.cls("", {isInSpaceEditor: feature === XyiconFeature.SpaceEditor})}>
			<div className="top-section">
				<div className="heading hbox">
					{!isAdvanced && (
						<SearchFieldV5
							value={search}
							onInput={onSearchInput}
							cyId={`${XyiconFeature[feature].charAt(0).toLowerCase() + XyiconFeature[feature].slice(1)}-filter-editor-search-field`} //TODO: if there are alot use cases to change characters use a utility function to handle this
						/>
					)}
					<FilterSwitchV5
						label={isAdvanced ? "Quick Filter" : "Advanced Filter"}
						value={isAdvanced}
						onChange={onChangeFilterType}
					/>
					{filters.type === "simple" && <div className="separator"></div>}
				</div>
				{filters.type === "advanced" ? (
					<AdvancedFilterEditorV5
						ref={advancedEditorRef}
						features={features}
						filters={appliedFilters}
						localFilters={local}
						fieldFilter={fieldFilter}
						syncFilters={syncFilters}
						syncLocalFilters={syncLocalFilters}
					/>
				) : (
					<SimpleFilterEditorV5
						ref={simpleEditorRef}
						features={features}
						feature={feature}
						filters={appliedFilters}
						localFilters={local}
						items={items}
						loader={loader}
						search={search}
						resetSearch={onResetSearch}
					/>
				)}
			</div>
		</FilterEditorStyled>
	);

	const style: CSSProperties = {
		width: "360px",
		maxHeight: "calc(100vh - 150px)",
	};

	if (isAdvanced) {
		style.width = "652px";
	}

	return isSpaceEditor ? (
		<>
			<PanelHeaderStyled>
				<div className="title">
					<FilterIcon />
					<h4>FILTER</h4>
				</div>
				<HeaderButtonsStyled>
					{headerButtons && <div className="header-buttons">{headerButtons}</div>}
					<IconButtonV5
						IconComponent={CloseIcon}
						onClick={onClose}
					/>
				</HeaderButtonsStyled>
			</PanelHeaderStyled>
			{filterContent}
		</>
	) : (
		<PopupV5
			onClose={onClose}
			label={
				<TitleStyled>
					<FilterIcon />
					{isAdvanced ? "ADVANCED FILTER" : "FILTER"}
				</TitleStyled>
			}
			className="Filter"
			style={style}
			parentRef={parentRef.current}
			noButtons={true}
			verticalAlignment={VerticalAlignment.bottomOuter}
			horizontalAlignment={HorizontalAlignment.left}
			offsetX={0}
			offsetY={8}
			headerButtons={headerButtons}
			isSmallPopup={true}
		>
			{filterContent}
		</PopupV5>
	);
});

export const HeaderButtonsStyled = styled.div`
	${FlexCenterStyle}
	justify-content: space-between;
	gap: ${baseDistance.sm};
`;

const TitleStyled = styled.div`
	display: flex;
	align-items: center;
	gap: ${baseDistance.sm};
	font-size: ${fontSize.lg};
`;
